import { useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  Typography,
  Switch,
  Skeleton,
} from '@mui/material';
import { useInstitutes } from '../../hooks/useInstitutes';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { InstituteSubscriptionPanel } from '../../components/InstituteSubscriptionPanel';
import type { InstituteListDto } from '../../types/institute';

// Add new feature flags here — no other code changes needed.
const KNOWN_FLAGS: { key: string; label: string; desc: string; destructive?: boolean }[] = [
  {
    key: 'fees',
    label: 'Fees Module',
    desc: 'Enable fee collection, fee structure, and fee reminders for this institute.',
    destructive: true,
  },
];

interface FlagRowProps {
  featureKey: string;
  label: string;
  desc: string;
  value: boolean;
  destructive?: boolean;
  disabled: boolean;
  onToggle: (featureKey: string, isEnabled: boolean) => void;
}

function FlagRow({ featureKey, label, desc, value, destructive, disabled, onToggle }: FlagRowProps) {
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleChange = (_: unknown, checked: boolean) => {
    if (destructive && !checked) {
      setPendingValue(checked);
      setConfirmOpen(true);
    } else {
      onToggle(featureKey, checked);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.75,
          px: 0.5,
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <Box sx={{ flex: 1, pr: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
            {desc}
          </Typography>
        </Box>
        <Switch
          checked={value}
          onChange={handleChange}
          disabled={disabled}
          size="small"
          color="primary"
        />
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        title={`Disable ${label}`}
        message={`Disabling "${label}" will immediately affect all users of this institute. This cannot be undone without re-enabling the flag. Are you sure?`}
        confirmLabel="Disable"
        severity="danger"
        onConfirm={() => {
          onToggle(featureKey, pendingValue!);
          setConfirmOpen(false);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

export default function FeatureFlagsPage() {
  const { useList, useFeatureFlags, useSetFeatureFlag } = useInstitutes();
  const [tab, setTab] = useState(0);
  const [selectedInstitute, setSelectedInstitute] = useState<InstituteListDto | null>(null);

  const { data: institutesData } = useList({ pageSize: 200 });
  const institutes = institutesData?.data ?? [];

  const { data: featureFlags = {}, isLoading: loadingFlags } = useFeatureFlags(
    selectedInstitute?.id ?? null
  );

  const setFeatureFlagMutation = useSetFeatureFlag(selectedInstitute?.id ?? 0);

  const handleToggle = (featureKey: string, isEnabled: boolean) => {
    if (!selectedInstitute) return;
    setFeatureFlagMutation.mutate({ featureKey, isEnabled });
  };

  return (
    <Box>
      {/* Institute selector */}
      <Paper
        sx={{
          p: 1.5,
          mb: 2,
          border: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Autocomplete
          options={institutes}
          getOptionLabel={(opt) => `${opt.instituteName} (${opt.code})`}
          value={selectedInstitute}
          onChange={(_, v) => setSelectedInstitute(v)}
          size="small"
          sx={{ minWidth: 320 }}
          renderInput={(params) => (
            <TextField {...params} label="Select Institute" placeholder="Search and select…" />
          )}
          renderOption={({ key, ...props }, option) => (
            <Box key={key} component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {option.instituteName}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                  {option.code}
                </Typography>
              </Box>
            </Box>
          )}
        />
        {selectedInstitute && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Showing flags for{' '}
            <strong style={{ color: '#F1F5F9' }}>{selectedInstitute.instituteName}</strong>
          </Typography>
        )}
      </Paper>

      {/* Tabs + content */}
      <Paper sx={{ border: '1px solid rgba(255,255,255,0.06)' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid rgba(255,255,255,0.07)', px: 2 }}
        >
          <Tab label="Feature Flags" />
          <Tab label="Subscription Settings" />
        </Tabs>

        {/* Feature Flags Tab */}
        {tab === 0 && (
          <Box sx={{ p: 2.5 }}>
            {!selectedInstitute ? (
              <Box
                sx={{
                  py: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="body1" sx={{ color: 'text.disabled' }}>
                  Select an institute to manage its feature flags
                </Typography>
              </Box>
            ) : loadingFlags ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: '8px' }} />
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {KNOWN_FLAGS.map((flag) => (
                  <FlagRow
                    key={flag.key}
                    featureKey={flag.key}
                    label={flag.label}
                    desc={flag.desc}
                    destructive={flag.destructive}
                    value={featureFlags[flag.key] ?? true}
                    disabled={setFeatureFlagMutation.isPending}
                    onToggle={handleToggle}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Subscription Settings Tab */}
        {tab === 1 && (
          <Box sx={{ p: 2.5 }}>
            {!selectedInstitute ? (
              <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ color: 'text.disabled' }}>
                  Select an institute to manage its subscription settings
                </Typography>
              </Box>
            ) : (
              <InstituteSubscriptionPanel instituteId={selectedInstitute.id} />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
